<template>
  <div class="mascot-container relative inline-block transition-transform duration-500" 
       :class="[size, message ? '-translate-x-1/3 md:translate-x-0 scale-90 md:scale-95' : 'scale-100']">
    <!-- Hand-drawn Style Owl Mascot -->
    <svg viewBox="0 0 200 200" class="w-full h-full" xmlns="http://www.w3.org/2000/svg" style="filter: drop-shadow(2px 4px 6px rgba(0, 0, 0, 0.15));">
      <!-- Body with sketch effect -->
      <path
        d="M100 35 C 65 35 35 65 35 115 C 35 165 65 185 100 185 C 135 185 165 165 165 115 C 165 65 135 35 100 35 Z"
        :fill="moodColors[mood].body"
        :stroke="moodColors[mood].stroke"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="transition-colors duration-500"
        style="paint-order: stroke fill;"
      />

      <!-- Ear tufts with hand-drawn lines -->
      <path
        d="M45 55 Q 35 35 30 25 Q 28 20 32 22 L 65 45"
        :fill="moodColors[mood].body"
        :stroke="moodColors[mood].stroke"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="transition-colors duration-500"
      />
      <path
        d="M155 55 Q 165 35 170 25 Q 172 20 168 22 L 135 45"
        :fill="moodColors[mood].body"
        :stroke="moodColors[mood].stroke"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="transition-colors duration-500"
      />

      <!-- Textured belly -->
      <ellipse cx="100" cy="130" rx="45" ry="50" fill="#f9f3e6" opacity="0.6" />
      <path
        d="M70 120 Q 100 115 130 120"
        fill="none"
        stroke="#d4c5a9"
        stroke-width="1.5"
        opacity="0.5"
      />
      <path
        d="M75 135 Q 100 130 125 135"
        fill="none"
        stroke="#d4c5a9"
        stroke-width="1.5"
        opacity="0.5"
      />

      <!-- Eyes with hand-drawn circles -->
      <g>
        <!-- Left eye white -->
        <circle cx="70" cy="90" r="22" fill="#fdfcf8" />
        <circle cx="70" cy="90" r="22" fill="none" :stroke="moodColors[mood].stroke" stroke-width="2.5" />

        <!-- Right eye white -->
        <circle cx="130" cy="90" r="22" fill="#fdfcf8" />
        <circle cx="130" cy="90" r="22" fill="none" :stroke="moodColors[mood].stroke" stroke-width="2.5" />
      </g>

      <!-- Pupils with personality -->
      <g :class="{'animate-pulse': mood === 'thinking'}">
        <ellipse
          :cx="70 + pupilOffset.x"
          :cy="90 + pupilOffset.y"
          rx="8"
          ry="10"
          fill="#2d1f1f"
          :class="{'animate-googly-left': mood === 'happy'}"
        />
        <ellipse
          :cx="130 + pupilOffset.x"
          :cy="90 + pupilOffset.y"
          rx="8"
          ry="10"
          fill="#2d1f1f"
          :class="{'animate-googly-right': mood === 'happy'}"
        />
        <!-- Eye highlights -->
        <circle :cx="72 + pupilOffset.x" :cy="87 + pupilOffset.y" r="3" fill="white" opacity="0.8" />
        <circle :cx="132 + pupilOffset.x" :cy="87 + pupilOffset.y" r="3" fill="white" opacity="0.8" />
      </g>

      <!-- Beak with texture -->
      <path
        d="M100 105 L88 120 L112 120 Z"
        fill="#d4a259"
        stroke="#a67c3d"
        stroke-width="2"
        stroke-linejoin="round"
      />
      <line x1="100" y1="105" x2="100" y2="120" stroke="#a67c3d" stroke-width="1.5" />

      <!-- Wings with feather texture -->
      <g :class="{'animate-wiggle': mood === 'happy'}">
        <!-- Left wing -->
        <path
          d="M35 105 Q 15 115 20 135 Q 22 145 35 135"
          :fill="moodColors[mood].wing"
          :stroke="moodColors[mood].stroke"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="transition-colors duration-500"
        />
        <path d="M30 110 Q 20 115 25 125" fill="none" :stroke="moodColors[mood].stroke" stroke-width="1.5" opacity="0.6" />

        <!-- Right wing -->
        <path
          d="M165 105 Q 185 115 180 135 Q 178 145 165 135"
          :fill="moodColors[mood].wing"
          :stroke="moodColors[mood].stroke"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="transition-colors duration-500"
        />
        <path d="M170 110 Q 180 115 175 125" fill="none" :stroke="moodColors[mood].stroke" stroke-width="1.5" opacity="0.6" />
      </g>

      <!-- Tail feathers -->
      <path
        d="M85 175 Q 75 185 70 195"
        fill="none"
        :stroke="moodColors[mood].stroke"
        stroke-width="2.5"
        stroke-linecap="round"
      />
      <path
        d="M100 178 Q 100 188 100 198"
        fill="none"
        :stroke="moodColors[mood].stroke"
        stroke-width="2.5"
        stroke-linecap="round"
      />
      <path
        d="M115 175 Q 125 185 130 195"
        fill="none"
        :stroke="moodColors[mood].stroke"
        stroke-width="2.5"
        stroke-linecap="round"
      />

      <!-- Feet -->
      <g>
        <path d="M80 175 L75 185 M80 175 L80 185 M80 175 L85 185" stroke="#d4a259" stroke-width="2.5" stroke-linecap="round" />
        <path d="M120 175 L115 185 M120 175 L120 185 M120 175 L125 185" stroke="#d4a259" stroke-width="2.5" stroke-linecap="round" />
      </g>
    </svg>

    <!-- Hand-drawn Speech Bubble -->
    <div
      v-if="message"
      class="absolute parchment-bg px-4 py-3 font-serif-body text-xs md:text-sm sepia-shadow animate-bounce border-2 border-amber-900/30 w-max max-w-[150px] md:max-w-[280px] text-center z-50
             top-1/2 -translate-y-1/2 left-[95%] ml-2
             md:inset-auto md:bottom-[95%] md:left-1/4 md:-translate-x-1/4 md:ml-0"
      style="border-radius: 18px;"
    >
      <span class="ink-text font-semibold break-words whitespace-normal">{{ message }}</span>
      <!-- Arrow -->
      <div class="absolute w-0 h-0
                  top-1/2 -translate-y-1/2 -left-[10px] border-t-[10px] border-t-transparent border-b-[10px] border-b-transparent border-r-[10px] border-r-[#f4e8d0]
                  md:inset-auto md:top-[98%] md:left-1/4 md:-translate-x-1/4 md:border-l-[10px] md:border-l-transparent md:border-r-[10px] md:border-r-transparent md:border-t-[14px] md:border-t-[#f4e8d0] md:border-b-0"></div>
    </div>
  </div>
</template>

<script setup>
import {computed} from 'vue'

const props = defineProps({
  mood: {
    type: String,
    default: 'neutral', // neutral, happy, sad, thinking
    validator: (v) => ['neutral', 'happy', 'sad', 'thinking'].includes(v)
  },
  size: {
    type: String,
    default: 'w-24 h-24 md:w-32 md:h-32'
  },
  message: {
    type: String,
    default: ''
  }
})


const moodColors = {
  neutral: { body: '#8b6f47', wing: '#6b5839', stroke: '#4a3829' },    // warm brown tones
  happy: { body: '#c9a961', wing: '#a68948', stroke: '#7a6334' },      // golden ochre
  sad: { body: '#9d7a5c', wing: '#7d5f47', stroke: '#5d4435' },        // muted brown
  thinking: { body: '#7b9095', wing: '#5f7377', stroke: '#465559' }    // slate blue-grey
}

const pupilOffset = computed(() => {
  switch (props.mood) {
    case 'thinking': return { x: 5, y: -5 }
    case 'happy': return { x: 0, y: -2 }
    case 'sad': return { x: 0, y: 5 }
    default: return { x: 0, y: 0 }
  }
})
</script>

<style scoped>
@keyframes wiggle {
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(10deg); }
  75% { transform: rotate(-10deg); }
}

@keyframes googly {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}

.animate-wiggle {
  animation: wiggle 0.5s ease-in-out infinite;
  transform-origin: center;
}

.animate-googly-left {
  animation: googly 0.6s ease-in-out infinite;
  transform-origin: center;
}

.animate-googly-right {
  animation: googly 0.8s ease-in-out infinite;
  animation-delay: 0.1s;
  transform-origin: center;
}
</style>
